import styles from "./MenuLayoutEdit.module.css";
import "./CustomAssignMenuLayoutKendo.css";
import React, { useEffect, useState } from "react";
import { get, post, patch } from "../../../Common/api";
import { useParams, useHistory } from "react-router";
import { setCurrentPageNotification, setNextPageNotification } from "../../../redux/notificationSlice";
import Alert, {
  getNotificationForError,
  success as successNotificationType,
  error as errorNotificationType,
} from "../../Alert";
import { useDispatch } from "react-redux";
import { columnTypes, direction } from "../../AdminGrid/AdminGrid";
import AdminGrid from "../../AdminGrid/AdminGrid";
import { Menu, MenuItem } from "@progress/kendo-react-layout";
import { orderBy } from "@progress/kendo-data-query";
import CancelMenuLayoutDialog from "./CancelMenuLayoutDialog";

const AssignMenuLayout = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const id = parseInt(useParams().id);
  const [title, setTitle] = useState("");
  const [isSaveEnabled, setIsSaveEnabled] = useState(true);
  const emptyArray = [];
  const [posTerminals, setPosTerminals] = useState(emptyArray);
  const [isDirty, setIsDirty] = useState(false);
  const [filterAvailableGrid, setFilterAvailableGrid] = useState([]);
  const [filterAssignedGrid, setFilterAssignedGrid] = useState([]);
  const [isCancelDialogVisible, setIsCancelDialogVisible] = useState(false);

  const [sort, setSort] = useState([
    { field: "districtName", dir: direction.ASC },
    { field: "schoolName", dir: direction.ASC },
    { field: "name", dir: direction.ASC },
  ]);

  const [assignedSort, setAssignedSort] = useState([
    { field: "name", dir: direction.ASC },
  ]);

  const gridType = {
    availableGrid: 1,
    assignedGrid: 2,
  };

  const searchChange = (event, grid) => {
    let newFilter = {
      logic: "or",
      filters: [
        {
          field: "name",
          operator: "startswith",
          value: event.target.value,
        },
        {
          field: "schoolName",
          operator: "startswith",
          value: event.target.value,
        },
        {
          field: "districtName",
          operator: "startswith",
          value: event.target.value,
        },
        {
          field: "menuLayoutTitle",
          operator: "startswith",
          value: event.target.value,
        },
      ],
    };
    if (grid === gridType.availableGrid) {
      setFilterAvailableGrid(newFilter);
    }
    if (grid === gridType.assignedGrid) {
      setFilterAssignedGrid(newFilter);
    }
  };

  const sortChange = (event) => {
    let sort = event.sort;

    // Do not sort by actions column
    if(sort[sort.length - 1].field === 'actions') {
      return;
    }

    sort.unshift(sort.pop());

    let posIndex = sort.findIndex((_) => _.field === "name");

    if (posIndex === 0 && sort.length > 1) {
      sort = sort.splice(posIndex, 1);
      sort[posIndex].dir = direction.ASC;
    }

    let schoolNameIndex = sort.findIndex((_) => _.field === "schoolName");
    let districtIndex = sort.findIndex((_) => _.field === "districtName");
    let layoutIndex = sort.findIndex((_) => _.field === "menuLayoutTitle");

    if (schoolNameIndex === 0) {
      if (districtIndex !== -1 || layoutIndex !== -1) {
        sort[schoolNameIndex].dir = direction.ASC;
      }
      sort = sort.splice(schoolNameIndex, 1);
      sort.push({ field: "name", dir: direction.ASC });
    }

    if (districtIndex === 0) {
      sort = sort.splice(districtIndex, 1);
      sort.push({ field: "schoolName", dir: direction.ASC });
      sort.push({ field: "name", dir: direction.ASC });
    }

    if (layoutIndex === 0) {
      sort = sort.splice(layoutIndex, 1);
      sort.push({ field: "name", dir: direction.ASC });
    }

    const layoutColumn = event.target.columns.find(
      (_) => _.field === "menuLayoutTitle"
    );
    if (layoutColumn) {
      setPosTerminals(sortTerminals(sort));
      setSort(sort);
    } else {
      setAssignedSort(sort);
    }
  };
  const sortTerminals = (sort) => {
    return orderBy(posTerminals, sort);
  };

  const filterOperators = {
    text: [
      {
        text: "grid.filterStartsWithOperator",
        operator: "startswith",
      },
    ],
  };

  const smartTrim = (obj) => {
    return typeof obj === "string" ? obj.trim() : obj;
  };

  useEffect(() => {
    get(`/menulayouts/${id}/title`)
      .then(({ title }) => {
        setTitle(title);
      })
      .catch((error) => {
        dispatch(setCurrentPageNotification(getNotificationForError(error)));
      });
    get(`/posterminals`)
      .then((terminals) => {
        const mappedTerminals = orderBy(
          terminals.map((t) => ({
            ...t,
            name: smartTrim(t.name),
            schoolName: smartTrim(t.schoolName),
            districtName: smartTrim(t.districtName),
            menuLayoutTitle: smartTrim(t.menuLayoutTitle),
            selectedAvailable: false,
            selectedAssigned: false,
          })),
          sort
        );

        setPosTerminals(mappedTerminals);
        setIsDirty(false);
      })
      .catch((error) => {
        dispatch(setCurrentPageNotification(getNotificationForError(error)));
      });
  }, [id, dispatch]);

  const columnsAvailableGrid = [
    {
      field: "selectedAvailable",
      title: "",
      type: columnTypes.checkbox,
      ariaLabelForHeaderCheckbox: "select all available pos terminals on page",
      ariaLabelledByField: "name",
      isFilterable: false,
      onChange: ({ column, page, data, headerColumnChecked }) => {
        const checked = !headerColumnChecked;
        const { skip, take } = page;
        const currentPage = data.slice(skip, skip + take);
        const isOnCurrentPage = (t) => {
          for (let i = 0; i < currentPage.length; i++) {
            if (currentPage[i].id === t.id) {
              return true;
            }
          }
          return false;
        };
        const newPosTerminals = posTerminals.map((t) =>
          isOnCurrentPage(t) ? { ...t, selectedAvailable: checked } : t
        );
        setPosTerminals(newPosTerminals);
      },
    },
    {
      field: "name",
      title: "POS",
      isFilterable: true,
    },
    {
      field: "schoolName",
      title: "School",
      isFilterable: true,
    },
    {
      field: "districtName",
      title: "District",
      isFilterable: true,
    },
    {
      field: "menuLayoutTitle",
      title: "Layout",
      isFilterable: true,
    },
    {
      field: "actions",
      title: "Actions",
      type: columnTypes.actions,
      isFilterable: false,
    },
  ];

  const columnsAssignedGrid = [
    {
      field: "selectedAssigned",
      title: "",
      type: columnTypes.checkbox,
      ariaLabelForHeaderCheckbox: "select all assigned pos terminals on page",
      ariaLabelledByField: "name",
      isFilterable: false,
      onChange: ({ column, page, data, headerColumnChecked }) => {
        const checked = !headerColumnChecked;
        const { skip, take } = page;
        const currentPage = data.slice(skip, skip + take);
        const isOnCurrentPage = (t) => {
          for (let i = 0; i < currentPage.length; i++) {
            if (currentPage[i].id === t.id) {
              return true;
            }
          }
          return false;
        };
        const newPosTerminals = posTerminals.map((t) =>
          isOnCurrentPage(t) ? { ...t, selectedAssigned: checked } : t
        );
        setPosTerminals(newPosTerminals);
      },
    },
    {
      field: "name",
      title: "POS",
      isFilterable: true,
    },
    {
      field: "schoolName",
      title: "School",
      isFilterable: true,
    },
    {
      field: "districtName",
      title: "District",
      isFilterable: true,
    },
    {
      field: "actions",
      title: "Actions",
      type: columnTypes.actions,
      isFilterable: false,
    },
  ];
  const removeTerminal = (terminal) => {
    const newTerminals = posTerminals.map((t) =>
      t.id === terminal.id
        ? {
            ...t,
            menuLayoutId: t.originalMenuLayoutId,
            menuLayoutTitle: t.originalMenuLayoutTitle,
            selectedAssigned: false,
            selectedAvailable: false,
          }
        : t
    );
    setPosTerminals(newTerminals);
    setIsDirty(true);
  };
  const addTerminal = (terminal) => {
    const newTerminals = posTerminals.map((t) =>
      t.id === terminal.id
        ? {
            ...t,
            originalMenuLayoutId: t.menuLayoutId,
            originalMenuLayoutTitle: t.menuLayoutTitle,
            menuLayoutId: id,
            menuLayoutTitle: title,
            selectedAvailable: false,
            selectedAssigned: false,
          }
        : t
    );
    setPosTerminals(newTerminals);
    setIsDirty(true);
  };
  const getActionsForAvailableTerminal = (terminal) => [
    {
      title: "Assign",
      label: `Add terminal ${terminal.name}`,
      onClick: (props) => {
        addTerminal(props.dataItem);
      },
    },
  ];
  const onChangeSelectedAvailable = (props) => {
    const id = props.dataItem.id;
    const value = !props.dataItem.selectedAvailable.value;
    const newTerminals = posTerminals.map((t) =>
      t.id === id
        ? {
            ...t,
            selectedAvailable: value,
          }
        : t
    );
    setPosTerminals(newTerminals);
  };
  const onChangeSelectedAssigned = (props) => {
    const id = props.dataItem.id;
    const value = !props.dataItem.selectedAssigned.value;
    const newTerminals = posTerminals.map((t) =>
      t.id === id
        ? {
            ...t,
            selectedAssigned: value,
          }
        : t
    );
    setPosTerminals(newTerminals);
  };

  const availableTerminals = posTerminals
    .filter((t) => t.menuLayoutId !== id)
    .map((t) => ({
      ...t,
      actions: getActionsForAvailableTerminal(t),
      selectedAvailable: {
        value: t.selectedAvailable,
        onChange: onChangeSelectedAvailable,
      },
    }));

  const getActionsForAssignedTerminal = (terminal) => [
    {
      title: "Remove",
      label: `Remove terminal ${terminal.name}`,
      onClick: (props) => {
        removeTerminal(props.dataItem);
      },
    },
  ];
  let assignedTerminals = posTerminals
    .filter((t) => t.menuLayoutId === id)
    .map((t) => ({
      ...t,
      actions: getActionsForAssignedTerminal(t),
      selectedAssigned: {
        value: t.selectedAssigned,
        onChange: onChangeSelectedAssigned,
      },
    }));
  assignedTerminals = orderBy(assignedTerminals, assignedSort);

  const addSelectedToLayout = () => {
    const selectedTerminals = posTerminals.filter(
      (t) => t.selectedAvailable && t.menuLayoutId !== id
    );
    const isThereSomeTerminalSelected = selectedTerminals.some(
      (nt) => nt.selectedAvailable
    );

    if (!isThereSomeTerminalSelected) {
      dispatch(
        setCurrentPageNotification({
          type: "error",
          message: "Please select a POS to Assign this Layout",
        })
      );
      window.scrollTo(0, 0);
    } else {
      const newTerminals = posTerminals.map((t) =>
        t.selectedAvailable && t.menuLayoutId !== id
          ? {
              ...t,
              originalMenuLayoutId: t.menuLayoutId,
              originalMenuLayoutTitle: t.menuLayoutTitle,
              menuLayoutId: id,
              menuLayoutTitle: title,
              selectedAvailable: false,
              selectedAssigned: false,
            }
          : t
      );
      setPosTerminals(newTerminals);
      setIsDirty(true);
    }
  };

  const selectAllAvailablePosTerminals = () => {
    const newPosTerminals = posTerminals.map((t) =>
      t.menuLayoutId !== id
        ? {
            ...t,
            selectedAvailable: true,
          }
        : t
    );
    setPosTerminals(newPosTerminals);
  };

  const unselectAllAvailablePosTerminals = () => {
    const newPosTerminals = posTerminals.map((t) =>
      t.menuLayoutId !== id
        ? {
            ...t,
            selectedAvailable: false,
          }
        : t
    );
    setPosTerminals(newPosTerminals);
  };

  const removeSelectedFromLayout = () => {
    const selectedLayouts = posTerminals.filter(
      (t) => t.selectedAssigned && t.menuLayoutId === id
    );
    if (selectedLayouts.length === 0) {
      dispatch(
        setCurrentPageNotification({
          type: errorNotificationType,
          message: "Please select a POS to remove this Layout",
        })
      );
      window.scrollTo(0, 0);
    } else {
      const newTerminals = posTerminals.map((t) =>
        t.selectedAssigned && t.menuLayoutId === id
          ? {
              ...t,
              menuLayoutId: t.originalMenuLayoutId,
              menuLayoutTitle: t.originalMenuLayoutTitle,
              selectedAvailable: false,
              selectedAssigned: false,
            }
          : t
      );
      setPosTerminals(newTerminals);
      setIsDirty(true);
    }
  };
  const handleSelectMenuItem = (e) => {
    if (e.item && e.item.data && e.item.data.callback) {
      e.item.data.callback();
    }
  };
  const saveAssignments = async () => {
    const assignments = posTerminals.map((t) => ({
      posId: t.id,
      menuLayoutId: t.menuLayoutId,
    }));
    setIsSaveEnabled(false);
    let success = false;
    try {
      await patch(
        `/PosTerminals`,
        assignments,
        "Failed to save the Menu Layout assignments"
      );
      dispatch(
        setNextPageNotification({
          type: successNotificationType,
          message: `Menu Layout ${title}'s Assignments have been updated`,
        })
      );
      success = true;
      setIsDirty(false);
      setIsSaveEnabled(true);
      window.scrollTo(0, 0);
      history.push("/menulayouts");
    } catch (error) {
      dispatch(setCurrentPageNotification(getNotificationForError(error)));
      setIsSaveEnabled(true);
      window.scrollTo(0, 0);
      const data = error && error.data;
      const { navigateToMenuLayoutList, reloadData } = data;
      if (navigateToMenuLayoutList) {
        dispatch(setNextPageNotification(getNotificationForError(error)));
        history.push("/menulayouts");
      } else if (reloadData) {
        get(`/posterminals`)
          .then((terminals) => {
            const mappedTerminals = orderBy(
              terminals.map((t) => ({
                ...t,
                name: smartTrim(t.name),
                schoolName: smartTrim(t.schoolName),
                districtName: smartTrim(t.districtName),
                menuLayoutTitle: smartTrim(t.menuLayoutTitle),
                selectedAvailable: false,
                selectedAssigned: false,
              })),
              sort
            );
            setPosTerminals(mappedTerminals);
            setIsDirty(false);
          })
          .catch((error) => {
            dispatch(setCurrentPageNotification(getNotificationForError(error)));
          });
      }
    }
    setIsSaveEnabled(true);
    return success;
  };

  const redirectToMenuLayoutList = () => {
    setIsCancelDialogVisible(false);
    history.push("/menulayouts");
  };

  const cancelAssignmentChanges = () => {
    if (isDirty) {
      setIsCancelDialogVisible(true);
    } else {
      redirectToMenuLayoutList();
    }
  };

  return (
    <React.Fragment>
      <Alert />
      {isCancelDialogVisible && (
        <CancelMenuLayoutDialog
          onOk={() => redirectToMenuLayoutList()}
          onCancel={() => setIsCancelDialogVisible(false)}
        />
      )}
      <div className="row MarginBottomDiv">
        <div className="col-md-12 section-spacing">
          <h2> {title} Assignments </h2>
          <h4>Available</h4>

          <div className="col-md-12">
            <div className="row">
              <div className="k-menu-grid col-md-12">
                <div>
                  <Menu
                    openOnClick={true}
                    onSelect={handleSelectMenuItem}
                    className="col-md-1 vf-no-space"
                    style={ {padding: 0} }
                  >
                    <MenuItem
                      text="Actions for available layouts"
                      render={() => <span className="k-icon k-i-menu" />}
                      style={ {padding: 0} }
                    >
                      <MenuItem
                        data={{ callback: addSelectedToLayout }}
                        text="Assign To Layout"
                      ></MenuItem>
                    </MenuItem>
                  </Menu>
                </div>                
                <ul
                  class="k-widget k-reset k-header k-menu-custome k-menu-horizontal col-md-2"
                  role="menubar"
                >
                  <li
                    class="k-item k-menu-item k-first"
                    tabindex="0"
                    role="menuitem"
                    aria-label="Select All"
                  >
                    <span class="k-link k-menu-link" role="presentation">
                      <a
                        aria-label="Select all the records on all of the pages of the Available Grid"
                        onClick={selectAllAvailablePosTerminals}
                      >
                        {" "}
                        Select All
                      </a>
                    </span>
                  </li>
                  <li
                    class="k-item k-menu-item"
                    tabindex="-1"
                    role="menuitem"
                    aria-label="|"
                  >
                    <span class="k-link k-menu-link" role="presentation">
                      |
                    </span>
                  </li>
                  <li
                    class="k-item k-menu-item"
                    tabindex="-1"
                    role="menuitem"
                    aria-label="Unselect All"
                  >
                    <span class="k-link k-menu-link" role="presentation">
                      <a
                        aria-label="Unselect all available pos terminals"
                        onClick={unselectAllAvailablePosTerminals}
                      >
                        Unselect All{" "}
                      </a>
                    </span>
                  </li>
                </ul>
                <div
                  className="form-group vf-insert-space col-md-3 col-md-offset-6"
                  style={{ marginBottom: "0px" }}
                >
                  <input
                    type="text"
                    aria-label="global search for available grid"
                    className="form-control"
                    placeholder="Search..."
                    onChange={(event) =>
                      searchChange(event, gridType.availableGrid)
                    }
                  />
                </div>

              </div>
            </div>
          </div>

          <AdminGrid
            columns={columnsAvailableGrid}
            data={availableTerminals}
            filter={filterAvailableGrid}
            filterChange={setFilterAvailableGrid}
            filterOperators={filterOperators}
            isFilterable={true}
            sortable={{ allowUnsort: false, mode: "multiple" }}
            sort={sort}
            sortChange={sortChange}
          />
          <h4>Assigned</h4>
          <div className="col-md-12">
            <div className="row">
              <div className="k-menu-grid col-md-12">
                <Menu
                  openOnClick={true}
                  onSelect={handleSelectMenuItem}
                  className="col-md-1"
                  style={ {padding: 0} }
                >
                  <MenuItem
                    text="Actions for assigned layouts"
                    render={() => <span className="k-icon k-i-menu" />}
                  >
                    <MenuItem
                      data={{ callback: removeSelectedFromLayout }}
                      text="Remove From Layout"
                    ></MenuItem>
                  </MenuItem>
                </Menu>                
                <div
                  className="form-group col-md-3 col-md-offset-8"
                  style={{ marginBottom: "0px" }}
                >
                  <input
                    type="text"
                    aria-label="global search for assigned grid"
                    placeholder="Search..."
                    className="form-control"
                    onChange={(event) =>
                      searchChange(event, gridType.assignedGrid)
                    }
                  />
                </div>
              </div>
            </div>
          </div>
          <AdminGrid
            columns={columnsAssignedGrid}
            data={assignedTerminals}
            filter={filterAssignedGrid}
            filterChange={setFilterAssignedGrid}
            filterOperators={filterOperators}
            isFilterable={true}
            sortable={{ allowUnsort: false, mode: "multiple" }}
            sort={assignedSort}
            sortChange={sortChange}
          />
          <div className={`button-wrap-alignment ${styles.buttonContainer}`}>
            <button
              aria-label="Save assignments"
              className={`btn button-width ${styles.success} col-md-2 col-md-offset-7`}
              onClick={() => saveAssignments()}
              disabled={!isSaveEnabled || !isDirty}
            >
              Save Assignments
            </button>
            <button
              aria-label="cancel"
              className="btn button-width col-md-2"
              onClick={() => cancelAssignmentChanges()}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};
export default AssignMenuLayout;
